import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { getApplicationStatus, reuploadDocument, resubmitApplication } from '../../services/driverService';

// Mirror of the upload allowlist used in the registration wizard / driverService.
const ALLOWED_DOC_EXT = ['jpg', 'jpeg', 'png', 'webp', 'pdf'];
const ALLOWED_DOC_MIME = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'application/pdf'];
const MAX_DOC_MB = 10;
function validateUploadFile(file) {
  if (!file) return { ok: false, reason: 'No file selected' };
  const ext = (file.name.split('.').pop() || '').toLowerCase();
  const mimeOk = file.type ? ALLOWED_DOC_MIME.includes(file.type.toLowerCase()) : false;
  const extOk = ALLOWED_DOC_EXT.includes(ext);
  if (!mimeOk && !extOk) return { ok: false, reason: 'Unsupported file type. Use JPEG, PNG, WebP, or PDF.' };
  if (file.size > MAX_DOC_MB * 1024 * 1024) return { ok: false, reason: `File too large (max ${MAX_DOC_MB}MB).` };
  return { ok: true };
}

// The full set of items an admin can flag for changes. Keys match the document
// fields used at registration and the editable user-doc fields.
const FLAGGABLE = [
  { key: 'licenceFront', label: 'Driver Licence (Front)', kind: 'doc' },
  { key: 'licenceBack', label: 'Driver Licence (Back)', kind: 'doc' },
  { key: 'proofOfIdentity', label: 'Proof of Identity', kind: 'doc' },
  { key: 'policeCheck', label: 'National Police Check', kind: 'doc' },
  { key: 'driverAuthority', label: 'Licence copy (showing PT Code)', kind: 'doc' },
  { key: 'wwccDocument', label: 'Working With Children Check', kind: 'doc' },
  { key: 'ctpInsurance', label: 'CTP Insurance (Green Slip)', kind: 'doc' },
  { key: 'comprehensiveInsurance', label: 'Comprehensive Insurance', kind: 'doc' },
  { key: 'vehicleRego', label: 'Vehicle Registration', kind: 'doc' },
  { key: 'licenceNumber', label: 'Licence Number', kind: 'field', type: 'text' },
  { key: 'licenceExpiry', label: 'Licence Expiry', kind: 'field', type: 'date' },
  { key: 'policeCheckIssueDate', label: 'Police Check Issue Date', kind: 'field', type: 'date' },
];

export default function DriverApplicationUpdate() {
  const navigate = useNavigate();
  const { currentUser, userProfile, refreshProfile } = useAuth();
  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [files, setFiles] = useState({});        // key -> File
  const [fieldVals, setFieldVals] = useState({}); // key -> value
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!currentUser) return;
    getApplicationStatus(currentUser.uid)
      .then(setApp)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [currentUser]);

  // Pre-fill editable fields from the current profile.
  useEffect(() => {
    if (userProfile) {
      setFieldVals((prev) => ({
        licenceNumber: userProfile.licenceNumber || '',
        licenceExpiry: userProfile.licenceExpiry || '',
        policeCheckIssueDate: userProfile.policeCheckIssueDate || '',
        ...prev,
      }));
    }
  }, [userProfile]);

  // Render only the items the admin flagged. If none were specified (legacy
  // request), fall back to letting the driver re-upload any document.
  const flagged = useMemo(() => {
    const list = Array.isArray(app?.changesRequestedFields) ? app.changesRequestedFields : [];
    if (list.length) return FLAGGABLE.filter((f) => list.includes(f.key));
    return FLAGGABLE.filter((f) => f.kind === 'doc');
  }, [app]);

  if (loading) {
    return <div className="flex justify-center p-20"><div className="spinner spinner-lg" /></div>;
  }

  // Only a "changes requested" application can be actioned here.
  if (!app || app.status !== 'changesRequested') {
    return (
      <div className="max-w-[500px] mx-auto px-6 py-8 text-center">
        <p className="text-body mb-4">There are no requested changes to action right now.</p>
        <Link to="/driver/application-status" className="text-brand-primary font-semibold">← Back to application status</Link>
      </div>
    );
  }

  const onPickFile = (key, file) => {
    if (!file) return;
    const chk = validateUploadFile(file);
    if (!chk.ok) {
      setErrors((e) => ({ ...e, [key]: chk.reason }));
      toast.error(chk.reason);
      return;
    }
    setErrors((e) => ({ ...e, [key]: null }));
    setFiles((f) => ({ ...f, [key]: file }));
  };

  const onSubmit = async () => {
    const errs = {};
    flagged.forEach((item) => {
      if (item.kind === 'doc' && !files[item.key]) errs[item.key] = 'Please re-upload this document';
      if (item.kind === 'field' && !String(fieldVals[item.key] || '').trim()) errs[item.key] = 'This field is required';
    });
    if (Object.keys(errs).length) {
      setErrors(errs);
      toast.error('Please complete the requested changes.');
      return;
    }
    setSubmitting(true);
    try {
      // Re-upload any changed documents.
      for (const item of flagged.filter((f) => f.kind === 'doc')) {
        if (files[item.key]) await reuploadDocument(currentUser.uid, files[item.key], item.key);
      }
      // Collect edited fields.
      const fieldUpdates = {};
      flagged.filter((f) => f.kind === 'field').forEach((item) => {
        fieldUpdates[item.key] = fieldVals[item.key];
      });
      await resubmitApplication(currentUser.uid, fieldUpdates);
      if (refreshProfile) await refreshProfile();
      toast.success('Application resubmitted for review.');
      navigate('/driver/application-status', { replace: true });
    } catch (err) {
      toast.error(err.message || 'Resubmission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-[600px] mx-auto px-6 py-8">
      <Link to="/driver/application-status" className="text-brand-primary font-semibold text-[0.9rem] mb-6 block">← Application status</Link>
      <h1 className="font-serif text-[1.6rem] mb-2">Update your application</h1>
      <p className="text-body text-[0.9rem] mb-5">Make the changes below, then resubmit. An admin will review your application again.</p>

      {/* Admin's note */}
      <div className="p-3.5 rounded-xl mb-6" style={{ background: '#fffbeb', border: '1px solid #fde68a' }}>
        <div className="font-bold mb-1" style={{ color: '#92400e' }}>What the reviewer asked for</div>
        <div className="text-[0.88rem] text-body leading-relaxed">
          {app.changesRequested || 'Please review and update the items below.'}
        </div>
      </div>

      <div className="flex flex-col gap-4">
        {flagged.map((item) => (
          <div key={item.key} className="p-4 rounded-2xl border border-slate-200 bg-white">
            <div className="font-semibold text-brand-dark text-[0.95rem] mb-2">{item.label}</div>

            {item.kind === 'doc' ? (
              <>
                <input
                  type="file"
                  accept=".jpg,.jpeg,.png,.webp,.pdf,application/pdf"
                  onChange={(e) => onPickFile(item.key, e.target.files?.[0])}
                  className="block w-full text-[0.85rem]"
                />
                {files[item.key] && (
                  <div className="text-[0.8rem] text-emerald-600 mt-1.5">✓ {files[item.key].name} ready to upload</div>
                )}
              </>
            ) : (
              <input
                type={item.type === 'date' ? 'date' : 'text'}
                value={fieldVals[item.key] || ''}
                max={item.type === 'date' ? undefined : undefined}
                onChange={(e) => setFieldVals((v) => ({ ...v, [item.key]: e.target.value }))}
                className={`form-input ${errors[item.key] ? 'error' : ''}`}
              />
            )}

            {errors[item.key] && <div className="form-error mt-1.5">{errors[item.key]}</div>}
          </div>
        ))}
      </div>

      <div className="flex gap-3 mt-8">
        <button onClick={() => navigate('/driver/application-status')} className="btn btn-secondary flex-1">Cancel</button>
        <button onClick={onSubmit} disabled={submitting} className="btn btn-primary flex-1">
          {submitting ? 'Resubmitting…' : 'Resubmit for review'}
        </button>
      </div>
    </div>
  );
}